// The bean rig's merged, skinned geometry (§12.4). The CORE geometry (one per look: hat × eyes ×
// detail, cached and shared by every rig wearing it) holds the body, eyes, lids/brows/lashes,
// feet, hands, noodle arms, identity rings, glyph badge, local chevron, danger rings, Dizzy stars,
// sweat, the ghost wisp tail and pebble, and the hat. Attachments that only some beans show at
// any moment (hot potato, balloon + string, parachute umbrella, crowns, boxing glove + spring) are
// separate small geometries on the same skeleton, drawn only while used. Parts are bones.
import {
  CircleGeometry, ConeGeometry, CylinderGeometry, DodecahedronGeometry, RingGeometry, Sphere, SphereGeometry, Vector3,
  type BufferGeometry,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { MODE, PAT, PartGeometryBuilder, type PartGeometry } from '../materials/partGeometry';
import { curve, extrude, helixSpring, M, smoothLathe, starPoints, taperedTube, teardrop } from './geo';
import { buildHat, buildPaperCrown, buildRoyalCrown, type HatInfo } from './hats';

/** Bone (part) indices. */
export const P = {
  ROOT: 0, BODY: 1, HEAD: 2, EYE_L: 3, EYE_R: 4, PUPIL_L: 5, PUPIL_R: 6, LID_L: 7, LID_R: 8,
  FOOT_L: 9, FOOT_R: 10, HAND_L: 11, HAND_R: 12, RING: 13, LOCAL_RING: 14, TEAM_RING: 15, GLYPH: 16,
  CHEVRON: 17, DANGER: 18, STOMP: 19, HAT_ANIM: 20, STAR0: 21, STAR1: 22, STAR2: 23, STAR3: 24,
  SWEAT_L: 25, SWEAT_R: 26, TAIL: 27, PEBBLE: 28, POTATO: 29, BALLOON: 30, PARACHUTE: 31,
  PAPER_CROWN: 32, CROWN: 33, GLOVE: 34, SPRING: 35, POTATO_SPARK: 36,
} as const;
export const PART_COUNT = 37;

/** Chain rings (§12.4: 10 rings per noodle arm), plus the balloon string. */
export const CHAIN_RINGS = 10;
export const BONE_ARM_L = PART_COUNT;
export const BONE_ARM_R = PART_COUNT + CHAIN_RINGS;
export const BONE_STRING = PART_COUNT + CHAIN_RINGS * 2;
export const BONE_COUNT = PART_COUNT + CHAIN_RINGS * 3;

/** §12.4 body lathe profile (radius, y). */
export const BODY_PROFILE: readonly [number, number][] = [
  [0, 0], [0.3, 0.02], [0.42, 0.1], [0.45, 0.3], [0.44, 0.55], [0.4, 0.8], [0.3, 0.98], [0.16, 1.07], [0, 1.1],
];
export const BODY_HEIGHT = 1.1;
export const HEAD_ANCHOR_Y = 1.05;
export const EYE_R = 0.13;
export const PUPIL_R = 0.065;
export const HAND_R = 0.07;
export const ARM_R = 0.055;
/**
 * Feet: flattened spheres, radii (x, y, z). A touch larger than §12.4's 0.16 × 0.08 × 0.22
 * extents and set forward, so the toes peek out from under the body at the gameplay pitch.
 */
export const FOOT = { x: 0.17, y: 0.055, z: 0.22, rx: 0.11, ry: 0.06, rz: 0.16 } as const;
export const RING = { r: 0.55, width: 0.08, localWidth: 0.12, teamR: 0.665, teamWidth: 0.05, lift: 0.025 } as const;
export const GLYPH_BADGE = { r: 0.19, ahead: 0.8 } as const;

/** §12.4 eye styles (catalog ids): scale and placement, plus lids, brows or lashes. */
export interface EyeStyle { scale: number; x: number; y: number; z: number; pupil: number; lids: boolean; brows: boolean; lashes: boolean }
export const EYE_STYLES: readonly EyeStyle[] = [
  { scale: 1, x: 0.12, y: 0.8, z: 0.36, pupil: 1, lids: false, brows: false, lashes: false },          // classic googly
  { scale: 1.38, x: 0.158, y: 0.82, z: 0.32, pupil: 1.25, lids: false, brows: false, lashes: false },  // giant
  { scale: 0.62, x: 0.1, y: 0.79, z: 0.39, pupil: 0.8, lids: false, brows: false, lashes: false },     // tiny
  { scale: 1, x: 0.12, y: 0.8, z: 0.36, pupil: 1, lids: true, brows: false, lashes: false },           // sleepy (half lids)
  { scale: 1, x: 0.12, y: 0.8, z: 0.36, pupil: 1, lids: false, brows: true, lashes: false },           // angry brows
  { scale: 1, x: 0.12, y: 0.8, z: 0.36, pupil: 1, lids: false, brows: false, lashes: true },           // lashes
];

export function eyeStyle(id: number): EyeStyle {
  return EYE_STYLES[id] ?? EYE_STYLES[0];
}

export interface CoreGeometry extends PartGeometry { hat: HatInfo }
export type AttachmentId = 'potato' | 'balloon' | 'parachute' | 'crowns' | 'glove';
export const ATTACHMENTS: readonly AttachmentId[] = ['potato', 'balloon', 'parachute', 'crowns', 'glove'];

const INKC = '#2A1A3A';
/** Rig-local bounds: body, rings, raised arms, balloon and umbrella all fit (Big Shake scales via the root). */
const BOUNDS = new Sphere(new Vector3(0, 1.0, 0), 2.7);

const coreCache = new Map<string, CoreGeometry>();
const attachCache = new Map<string, PartGeometry>();

function lowDetail(segments: number): boolean {
  return segments < 28;
}

/** The merged core geometry for a look; cached and shared by every rig with the same key. */
export function coreGeometry(segments: number, hat: number, eyes: number): CoreGeometry {
  const key = `${segments}:${hat}:${eyes}`;
  let g = coreCache.get(key);
  if (!g) {
    g = buildCore(segments, hat, eyes);
    coreCache.set(key, g);
  }
  return g;
}

export function attachmentGeometry(id: AttachmentId, segments: number): PartGeometry {
  const key = `${id}:${segments}`;
  let g = attachCache.get(key);
  if (!g) {
    g = buildAttachment(id, lowDetail(segments));
    attachCache.set(key, g);
  }
  return g;
}

function detail(lo: boolean): (n: number) => number {
  return (n) => Math.max(3, Math.round(lo ? n * 0.6 : n));
}

/**
 * A flat annulus on the ground (inner → outer radius), its radial coordinate in the band pattern
 * so the shader can ink both borders (no hull needed, ~100 triangles).
 */
function band(inner: number, outer: number, segments: number): BufferGeometry {
  const g = new RingGeometry(inner, outer, segments, 1);
  g.rotateX(-Math.PI / 2);
  const uv = g.getAttribute('uv');
  const pos = g.getAttribute('position');
  for (let i = 0; i < pos.count; i++) {
    const r = Math.hypot(pos.getX(i), pos.getZ(i));
    uv.setXY(i, (r - inner) / (outer - inner), 0);
  }
  return g;
}

function buildCore(segments: number, hatId: number, eyesId: number): CoreGeometry {
  const b = new PartGeometryBuilder();
  const lo = lowDetail(segments);
  const k = detail(lo);

  // Body (§12.4 lathe; the profile is smoothed through its points).
  b.add(smoothLathe(BODY_PROFILE, lo ? 13 : 20, segments), null, { part: P.BODY, mode: MODE.BODY });

  // Eyes in eye-local space (the style's scale lives in the part matrix).
  const style = eyeStyle(eyesId);
  for (const [eye, pupil, lid, side] of [[P.EYE_L, P.PUPIL_L, P.LID_L, -1], [P.EYE_R, P.PUPIL_R, P.LID_R, 1]] as const) {
    b.add(new SphereGeometry(EYE_R, k(18), k(12)), null, { part: eye, mode: MODE.EYE, color: '#FFFFFF', outline: 0.8 });
    b.add(new SphereGeometry(PUPIL_R, k(14), k(5), 0, Math.PI * 2, 0, Math.PI / 2), M(0, 0, 0, Math.PI / 2, 0, 0, 1, 0.45, 1), { part: pupil, mode: MODE.PUPIL, outline: 0 });
    if (style.lids) {
      // A body-tinted half shell over the top of the eye; the rig rotates it to droop and blink.
      b.add(new SphereGeometry(EYE_R * 1.08, k(18), k(6), 0, Math.PI * 2, 0, Math.PI / 2), null, { part: lid, mode: MODE.TINT, color: '#F4EEF2', outline: 0.8 });
    }
    if (style.brows) {
      b.add(new RoundedBoxGeometry(0.19, 0.055, 0.05, 2, 0.024), M(side * 0.01, EYE_R + 0.035, 0.07, 0, 0, side * 0.42), { part: lid, mode: MODE.VCOLOR, color: INKC, outline: 0 });
    }
    if (style.lashes) {
      for (let i = 0; i < 3; i++) {
        const a = 0.5 + i * 0.36;
        const x = side * Math.cos(a) * EYE_R * 0.97;
        const y = Math.sin(a) * EYE_R * 0.97;
        const c = new CylinderGeometry(0.009, 0.014, 0.08, 6);
        c.translate(0, 0.04, 0);
        b.add(c, M(x, y, 0.04, -0.35, 0, -side * (Math.PI / 2 - a) * 0.95), { part: lid, mode: MODE.VCOLOR, color: INKC, outline: 0 });
      }
    }
  }

  // Feet (body-tinted, a touch darker) and mitten hands.
  for (const [foot, hand] of [[P.FOOT_L, P.HAND_L], [P.FOOT_R, P.HAND_R]] as const) {
    b.add(new SphereGeometry(1, k(12), k(8)), M(0, 0, 0, 0, 0, 0, FOOT.rx, FOOT.ry, FOOT.rz), { part: foot, mode: MODE.TINT, color: '#B8AEB8' });
    b.add(new SphereGeometry(HAND_R, k(10), k(7)), null, { part: hand, mode: MODE.TINT, color: '#FFFFFF' });
  }
  // Noodle arms: 10 rings × 6 sides (§12.4).
  b.addChainTube(BONE_ARM_L, CHAIN_RINGS, 6, { part: P.HAND_L, mode: MODE.TINT, color: '#F2EEF2', outline: 0.75 });
  b.addChainTube(BONE_ARM_R, CHAIN_RINGS, 6, { part: P.HAND_R, mode: MODE.TINT, color: '#F2EEF2', outline: 0.75 });

  // Identity ring (§12.4: r 0.55, width 0.08 / 0.12 local), team ring, danger rings: flat inked bands.
  const ringSegs = lo ? 40 : 56;
  b.add(band(RING.r - RING.width / 2, RING.r + RING.width / 2, ringSegs), null, { part: P.RING, mode: MODE.IDENT_UNLIT, pat: PAT.BAND, outline: 0 });
  b.add(band(RING.r - RING.localWidth / 2, RING.r + RING.localWidth / 2, ringSegs), null, { part: P.LOCAL_RING, mode: MODE.IDENT_UNLIT, pat: PAT.BAND, outline: 0 });
  b.add(band(RING.teamR - RING.teamWidth / 2, RING.teamR + RING.teamWidth / 2, ringSegs), null, { part: P.TEAM_RING, mode: MODE.TEAM_UNLIT, pat: PAT.BAND, outline: 0 });
  b.add(band(0.52, 0.74, ringSegs), null, { part: P.DANGER, mode: MODE.DANGER, color: '#FF3B30', pat: PAT.GLOW_BAND, outline: 0 });
  b.add(band(0.5, 0.58, ringSegs), null, { part: P.STOMP, mode: MODE.DANGER, color: '#FF4D4D', pat: PAT.BAND, outline: 0 });
  const badge = new CircleGeometry(GLYPH_BADGE.r, lo ? 24 : 32);
  badge.rotateX(-Math.PI / 2);
  b.add(badge, null, { part: P.GLYPH, mode: MODE.GLYPH, pat: PAT.UV, outline: 0 });
  b.add(extrude([[-0.19, 0.13], [-0.085, 0.13], [0, 0.04], [0.085, 0.13], [0.19, 0.13], [0, -0.1]], 0.07, 0.016), null, { part: P.CHEVRON, mode: MODE.IDENT_UNLIT, outline: 1.2 });

  // Dizzy stars (§11.1: 2 / 3 / 4) and sweat drops (§2.7 ≥ 70).
  for (let i = 0; i < 4; i++) b.add(extrude(starPoints(0.085, 0.04), 0.035, 0.01), null, { part: P.STAR0 + i, mode: MODE.UNLIT, color: '#FFD84A', outline: 0.7 });
  b.add(teardrop(0.045, k(8)), null, { part: P.SWEAT_L, mode: MODE.VCOLOR, color: '#8FD8FF', outline: 0.6 });
  b.add(teardrop(0.045, k(8)), null, { part: P.SWEAT_R, mode: MODE.VCOLOR, color: '#8FD8FF', outline: 0.6 });

  // Ghost wisp tail (replaces the feet, §12.4) and the pebble-ready rock.
  b.add(taperedTube(curve([[0, 0.16, 0], [0, -0.06, -0.02], [0.07, -0.26, -0.08], [0.17, -0.38, -0.05]]), 0.3, 0.015, lo ? 8 : 11, k(11)), null, { part: P.TAIL, mode: MODE.TINT, color: '#FFFFFF' });
  b.add(new DodecahedronGeometry(0.11, 0), null, { part: P.PEBBLE, mode: MODE.VCOLOR, color: '#A9B3BF', solidGhost: true });

  // The hat, on the head anchor (§12.4: (0, 1.05, 0)).
  const hat = buildHat(b, hatId, { head: P.HEAD, anim: P.HAT_ANIM }, lo ? 0.6 : 1);
  return { ...b.build(BOUNDS), hat };
}

function buildAttachment(id: AttachmentId, lo: boolean): PartGeometry {
  const b = new PartGeometryBuilder();
  const k = detail(lo);
  switch (id) {
    case 'potato': {
      // The Hot Potato: a googly-eyed ticking bomb with a lit fuse (§4.2).
      b.add(new SphereGeometry(0.22, k(20), k(14)), null, { part: P.POTATO, mode: MODE.POTATO, color: '#3A2A4A' });
      b.add(new CylinderGeometry(0.075, 0.085, 0.07, k(14)), M(0, 0.215, 0), { part: P.POTATO, mode: MODE.VCOLOR, color: '#9AA9B8' });
      b.add(taperedTube(curve([[0, 0.24, 0], [0.02, 0.31, 0], [0.07, 0.35, 0.02], [0.11, 0.34, 0.03]]), 0.02, 0.016, 10, 6), null, { part: P.POTATO, mode: MODE.VCOLOR, color: '#E0C08A', outline: 0.6 });
      b.add(extrude(starPoints(0.075, 0.03, 6), 0.03, 0.006), null, { part: P.POTATO_SPARK, mode: MODE.SPARK, color: '#FFB020', outline: 0 });
      for (const side of [-1, 1]) {
        b.add(new SphereGeometry(0.07, k(12), k(10)), M(side * 0.08, 0.05, 0.17), { part: P.POTATO, mode: MODE.EYE, color: '#FFFFFF', outline: 0.7 });
        b.add(new SphereGeometry(0.035, k(10), k(8)), M(side * 0.075, 0.035, 0.232, 0, 0, 0, 1, 1, 0.5), { part: P.POTATO, mode: MODE.PUPIL, outline: 0 });
      }
      // A grin, so it reads as a character at a glance.
      b.add(taperedTube(curve([[-0.08, -0.06, 0.2], [0, -0.1, 0.215], [0.08, -0.06, 0.2]]), 0.014, 0.014, 8, 5), null, { part: P.POTATO, mode: MODE.VCOLOR, color: '#FFF6E9', outline: 0 });
      break;
    }
    case 'balloon': {
      // Balloon (origin at the knot) in the identity color, and its string on chain rings.
      b.add(smoothLathe([[0.0, 0.0], [0.05, 0.03], [0.23, 0.16], [0.31, 0.34], [0.27, 0.52], [0.13, 0.63], [0, 0.65]], 16, k(22)), M(0, 0.02, 0), { part: P.BALLOON, mode: MODE.IDENT, color: '#FFFFFF' });
      b.add(new SphereGeometry(1, k(10), k(6)), M(-0.1, 0.5, 0.18, 0, 0, 0.5, 0.05, 0.08, 0.02), { part: P.BALLOON, mode: MODE.UNLIT, color: '#FFFFFF', outline: 0 });
      b.add(new ConeGeometry(0.04, 0.06, k(10)), M(0, 0.0, 0), { part: P.BALLOON, mode: MODE.IDENT, color: '#D8D0DA', outline: 0.6 });
      b.addChainTube(BONE_STRING, CHAIN_RINGS, 4, { part: P.BALLOON, mode: MODE.VCOLOR, color: '#FFF6E9', outline: 0.4 });
      break;
    }
    case 'parachute': {
      // Respawn umbrella (§4.3): identity wedges on cream, origin at the grip, canopy 0.72 m up.
      const canopy = smoothLathe([[0.0, 0.22], [0.25, 0.19], [0.46, 0.1], [0.64, -0.02], [0.68, 0.0], [0.52, 0.15], [0.28, 0.27], [0.0, 0.3]], 16, k(28));
      b.add(canopy, M(0, 0.72, 0), { part: P.PARACHUTE, mode: MODE.IDENT, color: '#FFFFFF', color2: '#FFF6E9', pat: PAT.WEDGES, around: 16 });
      b.add(new CylinderGeometry(0.016, 0.016, 0.8, 6), M(0, 0.48, 0), { part: P.PARACHUTE, mode: MODE.VCOLOR, color: '#5B4A6B', outline: 0.5 });
      b.add(new SphereGeometry(0.035, 8, 6), M(0, 1.04, 0), { part: P.PARACHUTE, mode: MODE.VCOLOR, color: '#FFD24A', outline: 0.6 });
      b.add(taperedTube(curve([[0, 0.1, 0], [0, 0.0, 0], [0.03, -0.06, 0], [0.08, -0.05, 0], [0.09, 0.0, 0]]), 0.022, 0.022, 10, 6), null, { part: P.PARACHUTE, mode: MODE.VCOLOR, color: '#8A5A3A', outline: 0.6 });
      break;
    }
    case 'crowns':
      buildPaperCrown(b, P.PAPER_CROWN, lo ? 0.6 : 1);
      buildRoyalCrown(b, P.CROWN, 1.05, lo ? 0.6 : 1);
      break;
    case 'glove': {
      // Boxing glove (fist + thumb + cuff, facing +z) and the coil spring it rides (unit length along +z).
      b.add(new SphereGeometry(1, k(18), k(12)), M(0, 0, 0.11, 0, 0, 0, 0.17, 0.15, 0.18), { part: P.GLOVE, mode: MODE.VCOLOR, color: '#FF3B3B' });
      b.add(new SphereGeometry(1, k(12), k(8)), M(0.13, 0.05, 0.08, 0, 0, 0, 0.065, 0.055, 0.085), { part: P.GLOVE, mode: MODE.VCOLOR, color: '#FF3B3B' });
      b.add(new CylinderGeometry(0.105, 0.115, 0.09, k(16)), M(0, 0, -0.06, Math.PI / 2, 0, 0), { part: P.GLOVE, mode: MODE.VCOLOR, color: '#FFF6E9', color2: '#FF3B3B', pat: PAT.STRIPES, freq: 34, axis: 1 });
      b.add(helixSpring(7, 0.07, 0.015), null, { part: P.SPRING, mode: MODE.VCOLOR, color: '#C7D2DD', outline: 0.45 });
      break;
    }
  }
  return b.build(BOUNDS);
}
